<template>
    <div>
        <div class="actions-bar hide-on-print">
            <user-avatar class="avatar"/>

            <div @click="$emit('toggle_contents_list')" class="action">
                <i class="icon fas fa-stream"></i>
            </div>

            <div @click="$emit('toggle_comments')" class="action">
                <i class="icon fas fa-comments fa-flip-horizontal"></i>
                <div v-if="note.comments_count" class="badge">{{note.comments_count}}</div>
            </div>

            <div @click="$emit('toggle_attachments')" class="action">
                <i class="icon fas fa-paperclip"></i>
                <div v-if="attachments_count" class="badge">{{attachments_count}}</div>
            </div>

            <div
                @click="bookmark_note"
                class="action"
                :class="{'has-text-danger': note.bookmarked}">

                <i v-if="!bookmark.loading" class="icon fas fa-star"></i>
                <i v-else class="icon loader"></i>
            </div>

            <div @click="lock_note" class="action" :class="{'has-text-danger': note.is_locked}">
                <i v-if="!lock.loading" class="icon fas fa-lock"></i>
                <i v-else class="icon loader"></i>
            </div>

            <div v-if="note.card" class="action">
                <i v-if="!card.loading" @click="open_card" class="icon fas fa-columns"></i>
                <i v-else class="icon loader"></i>
            </div>

            <div
                class="dropdown is-right"
                :class="{'is-active': show_dropdown}"
                @mouseleave="show_dropdown = false">

                <i @click="show_dropdown = !show_dropdown"
                   class="icon fas fa-ellipsis-v action"></i>

                <div class="dropdown-menu">
                    <div class="dropdown-content">
                        <a v-if="space_topic_permission('NOTE_CREATE', note.topic)"
                           @click="duplicate_preferences"
                           class="dropdown-item">

                            <i class="icon fas fa-copy"></i>
                            Duplicate note
                        </a>

                        <a @click="$emit('toggle_revision')" class="dropdown-item">
                            <i class="icon fas fa-history"></i>
                            History
                        </a>

                        <a @click="print" class="dropdown-item">
                            <i class="icon fas fa-print"></i>
                            Print
                        </a>

                        <a v-if="!note.is_private && note.created_by.id === user.id"
                           :class="{'has-text-grey': note.is_locked}"
                           class="dropdown-item"
                           @click="make_private">

                            <i class="icon fas fa-user-shield"></i>
                            Make Private
                        </a>

                        <a @click="show_confirm = deletable"
                            :class="{'disabled': !deletable}"
                            class="dropdown-item">

                            <i v-if="!remove.loading" class="icon fas fa-trash-alt"></i>
                            <i v-else class="icon loader"></i>
                            Move to trash
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <note-duplicate-modal
            v-if="show_duplicate_modal"
            @close="show_duplicate_modal = false"
            @duplicate_note="duplicate_note"/>

        <base-confirm-modal
            v-if="show_confirm"
            text="Do you really want to move this note to trash?"
            @close="show_confirm = false"
            @ok="remove_note"/>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import UserAvatar from '../user/UserAvatar.vue';
import { copy } from '../../utils/helpers';
import { RELOAD_NOTES_LIST, Event } from '../../utils/events';
import NoteDuplicateModal from './NoteDuplicateModal.vue';

export default {
    name: 'NoteDetailsActions',
    props: ['space', 'note', 'show_sidebars'],
    components: { UserAvatar, NoteDuplicateModal },
    data() {
        return {
            show_dropdown: false,
            show_confirm: false,
            show_duplicate_modal: false,
        };
    },
    computed: {
        ...mapState('user', ['user']),
        ...mapState('notes', ['remove', 'bookmark', 'lock', 'duplicate']),
        ...mapState('cards', { card: 'detail' }),
        deletable() {
            const { topic, is_locked } = this.note;
            return this.space_topic_permission('NOTE_DELETE', topic) && !is_locked;
        },
        attachments_count() {
            return this.note.attachments ? this.note.attachments.length : null;
        },
        is_tree_sidebar() {
            return 'topic' in this.$route.query || 'private' in this.$route.query;
        },
    },
    methods: {
        print() {
            window.print();
        },
        lock_note() {
            this.$store.dispatch('notes/lock', {
                space_id: this.space.id,
                note_id: this.note.id,
                is_locked: !this.note.is_locked,
            });
        },
        async open_card() {
            await this.$store.dispatch('cards/detail', {
                flow_id: this.note.card.flow_id,
                card_id: this.note.card.id,
                space_id: this.space.id,
            });

            this.$emit('toggle_card_details', this.card.data);
        },
        async bookmark_note() {
            await this.$store.dispatch('notes/bookmark', {
                space_id: this.space.id,
                note_id: this.note.id,
                remove: this.note.bookmarked,
            });

            if ('bookmarked' in this.$route.query) {
                Event.$emit(RELOAD_NOTES_LIST);
            }
        },
        async remove_note() {
            await this.$store.dispatch('notes/remove', {
                space_id: this.space.id,
                note_id: this.note.id,
            });

            if (this.is_tree_sidebar) {
                this.$store.commit('tree/remove_item', { id: this.note.id, kind: 'note' });
            }

            const query = copy(this.$route.query);
            query.note = undefined;
            this.$router.replace({
                name: 'content',
                params: this.$route.params,
                query,
            });
        },
        async make_private() {
            if (this.note.is_locked) return;

            await this.$store.dispatch('notes/update', {
                space_id: this.space.id,
                note_id: this.note.id,
                patch: true,
                data: {
                    topic: null,
                    is_private: true,
                    collection: null,
                },
            });

            this.$router.push({
                name: 'content',
                params: this.$route.params,
                query: { private: null, note: this.note.id },
            });
        },
        duplicate_preferences() {
            if (this.note.attachments.length > 0) {
                this.show_duplicate_modal = true;
            } else {
                this.duplicate_note();
            }
        },
        async duplicate_note(duplicate_attachments = false) {
            const { id } = await this.$store.dispatch('notes/duplicate', {
                space_id: this.space.id,
                note_id: this.note.id,
                data: { duplicate_attachments },
                is_tree_sidebar: this.is_tree_sidebar,
            });

            // Redirect to new note
            const query = { ...this.$route.query, note: id };
            this.$router.push({ name: 'content', params: this.$route.params, query });
        },
    },
};
</script>

<style scoped>
    .actions-bar {
        padding: 1rem;
    }

    .actions-bar .avatar {
        margin-bottom: 6rem;
    }

    .action {
        font-size: 1.3rem;
        color: #848484;
        cursor: pointer;
        padding-left: 0.3rem;
        margin-top: 0.6rem;
    }

    .action.disabled {
        color: #c0c0c0;
    }

    .action:hover {
        color: #1a7314;
    }

    .badge {
        position: absolute;
        margin-top: -25px;
        font-size: 0.3em;
        background: #83342c;
        padding: 0 0.2rem;
        color: #fff;
        border-radius: 1rem;
    }

</style>
